import React, {useEffect, useState} from 'react';

import {LOAD_BAR} from '../../constants/loadBar';
import {RECAPTCHA_ACTIONS} from '../../constants/recaptcha';
import {useRecaptchaProtection} from '../../hooks/useRecaptchaProtection';
import {analyticsService} from '../../services/analytics';
import {sendTerms} from '../../services/Api';
import {useChatStore} from '../../store/slices/chatStore';
import {useEcStore} from '../../store/slices/ecStore';
import {useInteractionStore} from '../../store/slices/interactionStore';
import {useTermsStore} from '../../store/slices/termsStore';
import {MerchantAccreditationPolicy} from '../MerchantAccreditationPolicy';
import {TermsConditions} from '../TermsConditions';
import './TermsTabs.style.scss';

export const TermsTabs = () => {
  const ecStateId = useEcStore((state) => state.ec.id);
  const isReceiptTermAccepted = useTermsStore(
    (state) => state.isReceiptTermAccepted
  );
  const isSimulationAccepted = useTermsStore(
    (state) => state.isSimulationAccepted
  );
  const isAccreditationPolicyAccepted = useTermsStore(
    (state) => state.isAccreditationPolicyAccepted
  );
  const setOpenCloseModalTerms = useTermsStore(
    (state) => state.setOpenCloseModalTerms
  );
  const accreditationPolicyAccepted = useTermsStore(
    (state) => state.accreditationPolicyAccepted
  );
  const receiptTermAccepted = useTermsStore(
    (state) => state.receiptTermAccepted
  );
  const simulationAccepted = useTermsStore((state) => state.simulationAccepted);
  const setMessages = useChatStore((state) => state.setMessages);
  const setChatStageFlow = useChatStore((state) => state.setChatStageFlow);
  const responseFlowChange = useChatStore((state) => state.responseFlowChange);
  const responseTypeChange = useChatStore((state) => state.responseTypeChange);
  const setDotTyping = useInteractionStore((state) => state.setDotTyping);
  const [tabSelect, setTabSelect] = useState('policy');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {executeWithProtection} = useRecaptchaProtection();

  useEffect(() => {
    analyticsService.trackTermsModalView('policy');
  }, []);

  const acceptTerms = (doc) => {
    if (doc === 'policy') {
      accreditationPolicyAccepted(true);
      if (!isReceiptTermAccepted) setTabSelect('terms');
    }

    if (doc === 'terms') {
      receiptTermAccepted(true);
      if (!isAccreditationPolicyAccepted) setTabSelect('policy');
    }
  };

  useEffect(() => {
    if (
      isAccreditationPolicyAccepted &&
      isReceiptTermAccepted &&
      !isSimulationAccepted &&
      !isSubmitting
    ) {
      setIsSubmitting(true);
      setDotTyping(true);
      setOpenCloseModalTerms(false);

      const termsData = {
        isAccreditationPolicyAccepted,
        isReceiptTermAccepted,
        isSimulationAccepted: true
      };

      executeWithProtection(
        (payload, token) => sendTerms(ecStateId, payload, token),
        RECAPTCHA_ACTIONS.TERMS_ACCEPTANCE,
        termsData
      )
        .then(() => {
          simulationAccepted(true);
          sessionStorage.setItem('accreditation_completed', 'true');
          analyticsService.trackAccreditationComplete();
          setMessages([
            {
              text: '',
              type: 'chat-bot-finish'
            }
          ]);
          setChatStageFlow(LOAD_BAR.TERMS);
          responseFlowChange('hidden');
          responseTypeChange('disabled');
        })
        .catch(() => {
          accreditationPolicyAccepted(false);
          receiptTermAccepted(false);
          simulationAccepted(false);
          setMessages([
            {
              text: 'Ops, algo deu errado... :('
            },
            {
              text: 'Por conta de um erro sistêmico, não foi possível completar sua solicitação. Por favor, aceite os termos novamente.'
            }
          ]);
        })
        .finally(() => {
          setDotTyping(false);
          setIsSubmitting(false);
        });
    }
  }, [
    isAccreditationPolicyAccepted,
    isReceiptTermAccepted,
    isSimulationAccepted,
    isSubmitting,
    ecStateId,
    executeWithProtection,
    setDotTyping,
    setOpenCloseModalTerms,
    simulationAccepted,
    setMessages,
    setChatStageFlow,
    responseFlowChange,
    responseTypeChange,
    accreditationPolicyAccepted,
    receiptTermAccepted
  ]);

  return (
    <section className="terms">
      <div className="tab">
        <button
          id="button-tab-policy"
          onClick={() => {
            setTabSelect('policy');
            analyticsService.trackTermsModalView('policy');
          }}
          className={tabSelect === 'policy' ? 'active' : ''}
          disabled={isSubmitting}
        >
          Política de credenciamento do estabelecimento comercial
        </button>
        <button
          id="button-tab-terms"
          onClick={() => {
            setTabSelect('terms');
            analyticsService.trackTermsModalView('terms');
          }}
          className={tabSelect === 'terms' ? 'active' : ''}
          disabled={isSubmitting}
        >
          Termos e Condições de Credenciamento
        </button>
      </div>

      <div
        id="policy"
        className={`tab-content ${
          tabSelect === 'policy' ? 'tab-content-show' : ''
        }`}
      >
        <div className="box-text-1">
          <article className="box-text-2">
            <MerchantAccreditationPolicy />
          </article>
        </div>
        <div className="btn-content-terms">
          {!isAccreditationPolicyAccepted && (
            <button
              className="btn-terms"
              id="btn-accept-terms-policy"
              onClick={() => acceptTerms('policy')}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'PROCESSANDO...' : 'ACEITO'}
            </button>
          )}
          {isAccreditationPolicyAccepted && (
            <div className="tooltip-modal-finish">
              <span>
                Você já aceitou a Política de Credenciamento do Estabelecimento
                Comercial!
              </span>

              <button
                className="btn-disabled"
                id="btn-accept-terms-policy-done"
                disabled="disabled"
              >
                ACEITO
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        id="terms"
        className={`tab-content ${
          tabSelect === 'terms' ? 'tab-content-show' : ''
        }`}
      >
        <div className="box-text-1">
          <article className="box-text-2">
            <TermsConditions />
          </article>
        </div>
        <div className="btn-content-terms">
          {!isReceiptTermAccepted && (
            <button
              className="btn-terms"
              id="btn-accept-terms"
              onClick={() => acceptTerms('terms')}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'PROCESSANDO...' : 'ACEITO'}
            </button>
          )}
          {isReceiptTermAccepted && (
            <div className="tooltip-modal-finish">
              <span>
                Você ja aceitou os termos e condições de credenciamento!
              </span>

              <button
                className="btn-disabled"
                id="btn-accept-terms-done"
                disabled="disabled"
              >
                ACEITO
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
